/**
 * Derive nutritional "flags" (warnings + positives) for a food, purely from its per-100g values —
 * no network call. Thresholds follow common label conventions (EU/UK FSA "traffic light" + the
 * EU "source of / high in" nutrition-claim rules), expressed per 100g.
 *
 * Returns an array of { key, label, tone } where tone is "warn" | "good".
 */

// Resolve the per-100g basis serving (same logic as the nutrient table: prefer 100g, else default).
const resolveHundredGram = (food) => {
  const servings = food.servings || [];
  const is100 = (s) => {
    const n = (s.name || "").replace(/\s/g, "").toLowerCase();
    return s.gramsPerServing === 100 || (n.includes("100") && (n.includes("g") || n.includes("גרם") || n.includes("ml")));
  };
  const hundred = servings.find(is100);
  if (hundred) return hundred;
  // Fall back to the default serving, scaled to 100g, so thresholds stay comparable.
  const def = servings.find((s) => s.isDefault) || servings[0];
  return def || null;
};

const microAmount = (serving, code) => {
  const m = (serving?.micronutrients || []).find((x) => x.code === code);
  return m?.amount;
};

// Scale a per-serving value to per-100g (so thresholds are apples-to-apples).
const per100 = (value, grams) => {
  if (value == null) return null;
  if (!grams || grams === 100) return value;
  return (value / grams) * 100;
};

export const getHealthFlags = (food) => {
  const s = resolveHundredGram(food);
  if (!s) return [];
  const nv = s.nutritionValues || s;
  const g = s.gramsPerServing || 100;

  const protein = per100(nv.protein, g);
  const fats = per100(nv.fats, g);
  const calories = per100(nv.calories, g);
  const sugar = per100(microAmount(s, "SUGAR"), g);
  const fiber = per100(microAmount(s, "FIBER"), g);
  const sodium = per100(microAmount(s, "SODIUM"), g); // mg per 100g
  const satFat = per100(microAmount(s, "SATURATED_FAT"), g);
  const transFat = per100(microAmount(s, "TRANS_FAT"), g);

  const flags = [];

  // ── Warnings (red) ──
  if (sugar != null && sugar > 22.5) flags.push({ key: "high-sugar", label: "עתיר בסוכר", tone: "warn" });
  // Sodium 600mg/100g ≈ 1.5g salt — the FSA "high" line.
  if (sodium != null && sodium > 600) flags.push({ key: "high-salt", label: "עתיר במלח", tone: "warn" });
  // FSA "high" saturated fat is >5g/100g. Prefer the real sat-fat value; fall back to total fat.
  if (satFat != null && satFat > 5) flags.push({ key: "high-sat-fat", label: "עתיר בשומן רווי", tone: "warn" });
  else if (satFat == null && fats != null && fats > 17.5) flags.push({ key: "high-fat", label: "עתיר בשומן", tone: "warn" });
  // Any meaningful trans fat is a red flag.
  if (transFat != null && transFat >= 0.5) flags.push({ key: "trans-fat", label: "מכיל שומן טראנס", tone: "warn" });

  // ── Positives (green) ──
  // EU claim: "high protein" if ≥20% of energy from protein; simplified to a solid per-100g cut.
  if (protein != null && protein >= 20) flags.push({ key: "high-protein", label: "עשיר בחלבון", tone: "good" });
  else if (protein != null && protein >= 12) flags.push({ key: "source-protein", label: "מקור לחלבון", tone: "good" });

  if (fiber != null && fiber >= 6) flags.push({ key: "high-fiber", label: "עשיר בסיבים", tone: "good" });
  else if (fiber != null && fiber >= 3) flags.push({ key: "source-fiber", label: "מקור לסיבים", tone: "good" });

  if (calories != null && calories > 0 && calories <= 40) flags.push({ key: "low-cal", label: "דל קלוריות", tone: "good" });
  if (sugar != null && sugar <= 5) flags.push({ key: "low-sugar", label: "דל סוכר", tone: "good" });

  return flags;
};
